import { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createProduct, getAdminProducts, getProductById, reset, updateProduct } from '../../features/product/productSlice';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import Loading from '../../components/Loading';
import 'react-quill/dist/quill.snow.css';

const COUNTRY_OPTIONS = [
  { value: 'latvia', label: 'Latvia' },
  { value: 'uk', label: 'United Kingdom' },
];

function AddProduct() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { isLoading, product, products } = useSelector((state) => state.product);
  const isEditing = Boolean(id);

  // Refs for file inputs
  const mainImageInputRef = useRef(null);
  const galleryImagesInputRef = useRef(null);

  // Core form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [mainImage, setMainImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [displayOrder, setDisplayOrder] = useState(999);
  
  // Image preview states
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [galleryImagePreviews, setGalleryImagePreviews] = useState([]);
  
  // State for product type and variations
  const [productType, setProductType] = useState('simple');
  const [options, setOptions] = useState([{ name: '', values: '' }]);
  const [variants, setVariants] = useState([{ price: '', countInStock: '', isActive: true }]);
  const [disabledVariantKeys, setDisabledVariantKeys] = useState([]);

  const buildVariantKey = (optionValues = []) =>
    optionValues
      .filter((opt) => opt?.name && opt?.value)
      .map((opt) => `${opt.name}=${opt.value}`)
      .sort()
      .join('|');
  const toggleCountrySelection = (value) => {
    setSelectedCountries((prev) => {
      if (prev.includes(value)) {
        return prev.filter((countryValue) => countryValue !== value);
      }
      return [...prev, value];
    });
  };

  // --- Logic for Variable Products ---
  const categoryOptions = useMemo(() => {
    if (!Array.isArray(products)) return [];
    const unique = new Set();
    products.forEach((item) => {
      if (item?.category) unique.add(item.category);
    });
    return Array.from(unique).sort();
  }, [products]);

  const generatedCombinations = useMemo(() => {
    if (productType !== 'variable') return [];

    const validOptions = options.filter(opt => opt.name && opt.values);
    if (validOptions.length === 0) return [];
    
    const cartesian = (sets) => {
      return sets.reduce((acc, set) => acc.flatMap(x => set.map(y => [...x, y])), [[]]);
    };
    
    const optionSets = validOptions.map(opt => 
      opt.values.split(',').map(val => ({ name: opt.name, value: val.trim() }))
    );

    return cartesian(optionSets);
  }, [options, productType]);

  const disabledVariantKeysRef = useRef(disabledVariantKeys);
  useEffect(() => {
    disabledVariantKeysRef.current = disabledVariantKeys;
  }, [disabledVariantKeys]);

  useEffect(() => {
    if (productType === 'variable') {
      const disabledSet = new Set(disabledVariantKeysRef.current);
      const newVariants = generatedCombinations.map(combo => {
        const existingVariant = variants.find(v => 
          JSON.stringify(v.optionValues) === JSON.stringify(combo)
        );
        const key = buildVariantKey(combo);
        return {
          optionValues: combo,
          price: existingVariant?.price || '',
          countInStock: existingVariant?.countInStock || '',
          image: existingVariant?.image || null,
          isActive: existingVariant
            ? existingVariant?.isActive !== false
            : !disabledSet.has(key),
        };
      });
      setVariants(newVariants);
    }
  }, [generatedCombinations, productType]);
  
  // --- Effect to fetch product data on load (for editing) ---
  useEffect(() => {
    dispatch(getAdminProducts())
    if (id) {
        dispatch(getProductById(id));
    }
    
    // On component unmount, reset the state
    return () => {
        dispatch(reset());
    };
  }, [id, dispatch]);

  // --- Effect to populate form when product data is loaded ---
  useEffect(() => {
    if (product && id) {
        setTitle(product.title || '');
        setDescription(product.description || '');
        setCategory(product.category || '');
        setSelectedCountries(product.countries || []);
        const resolvedOrder = Number(product.display_order);
        setDisplayOrder(Number.isFinite(resolvedOrder) ? resolvedOrder : 999);
        setProductType(product.productType || 'simple');
        setDisabledVariantKeys(
            Array.isArray(product.disabledVariantKeys) ? product.disabledVariantKeys : []
        );
        
        // Handle options - convert array values back to comma-separated strings
        if (product.options && product.options.length > 0) {
            setOptions(product.options.map(opt => ({
                name: opt.name,
                values: opt.values.join(', ')
            })));
        } else {
            setOptions([{ name: '', values: '' }]);
        }
        
        // Convert prices from cents to euros for display AND ensure isActive is set
        if (product.variants && product.variants.length > 0) {
        setVariants(product.variants.map(v => ({
                ...v,
                price: v.price / 100,
                discount_price: v.discount_price ? v.discount_price / 100 : '',
                isActive: v.isActive !== undefined ? v.isActive : true, // Default to true if not set
            })));
        }

        // Handle images - these are URLs from Firebase
        if (product.image) {
            const originalImageSrc = product.image.original?.src || product.image.src;
            setMainImage(originalImageSrc);
            setMainImagePreview(product.image.src || originalImageSrc);
        }
        
        if (product.images && product.images.length > 0) {
            const imageUrls = product.images.map(img => img.original?.src || img.src);
            const previewUrls = product.images.map(img => img.src);
            setGalleryImages(imageUrls);
            setGalleryImagePreviews(previewUrls);
        }
    }
  }, [product, id]);

  // --- Event Handlers ---
  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
        setMainImage(file);
        setMainImagePreview(URL.createObjectURL(file));
    }
  };

  const handleGalleryImagesChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const newPreviews = newFiles.map(file => URL.createObjectURL(file));
    
    setGalleryImages(prev => [...prev, ...newFiles]);
    setGalleryImagePreviews(prev => [...prev, ...newPreviews]);
  };

  const handleProductTypeChange = (e) => {
    const newType = e.target.value;
    setProductType(newType);
    if (newType === 'simple') {
      setVariants([{ price: '', countInStock: '', isActive: true }]);
      setDisabledVariantKeys([]);
    } else {
      setVariants([]);
    }
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...options];
    newOptions[index][field] = value;
    setOptions(newOptions);
  };

  const addOption = () => setOptions([...options, { name: '', values: '' }]);

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...variants];
    newVariants[index][field] = value;
    if (field === 'isActive' && newVariants[index].optionValues) {
        const key = buildVariantKey(newVariants[index].optionValues);
        if (key) {
            setDisabledVariantKeys((prev) => {
                if (value) {
                    return prev.filter((item) => item !== key);
                }
                if (prev.includes(key)) {
                    return prev;
                }
                return [...prev, key];
            });
        }
    }
    setVariants(newVariants);
  };

  const handleVariantImageChange = (index, file) => {
    const newVariants = [...variants];
    newVariants[index].image = file;
    setVariants(newVariants);
  };

  const removeGalleryImage = (indexToRemove) => {
    setGalleryImages(prev => prev.filter((_, index) => index !== indexToRemove));
    setGalleryImagePreviews(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  // 1. Add a Ref for the wrapper (to detect clicks outside)
  const categoryWrapperRef = useRef(null);

  // 2. Add State to toggle the dropdown visibility
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // 3. Add Filter Logic (filters options based on what you type)
  const filteredCategories = useMemo(() => {
    if (!category) return categoryOptions;
    return categoryOptions.filter(opt => 
        opt.toLowerCase().includes(category.toLowerCase())
    );
  }, [category, categoryOptions]);

  // 4. Add Effect to close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (categoryWrapperRef.current && !categoryWrapperRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validVariantKeys = new Set(
        generatedCombinations.map((combo) => buildVariantKey(combo)).filter(Boolean)
    );
    const filteredDisabledKeys = disabledVariantKeys.filter((key) => validVariantKeys.has(key));

    const finalVariants = variants
      .filter(v => v.isActive && v.price && v.countInStock)
      .map(v => ({
        price: Number(v.price) * 100,
        countInStock: Number(v.countInStock),
        image: v.image,
        optionValues: v.optionValues || [],
      }));

    if (finalVariants.length === 0) {
      toast.error('Please add and activate at least one variant with a price and stock.');
      return;
    }

    const parsedDisplayOrder = Number(displayOrder);
    const productData = {
      title, 
      description, 
      category,
      countries: selectedCountries,
      productType,
      image: mainImage,
      images: galleryImages,
      display_order: Number.isFinite(parsedDisplayOrder) ? parsedDisplayOrder : 999,
      options: productType === 'variable' ? 
        options.filter(opt => opt.name && opt.values).map(opt => ({
          name: opt.name, 
          values: opt.values.split(',').map(s => s.trim())
        })) : [],
      disabledVariantKeys: productType === 'variable' ? filteredDisabledKeys : [],
      variants: finalVariants,
    };
    
    // Dispatch create or update based on whether we have an id
    if (id) {
        dispatch(updateProduct({ id, productData }));
    } else {
        dispatch(createProduct(productData));
    }
};

  // --- React Quill Configuration ---
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['clean']
    ],
  };

  const isWaitingForProduct = isEditing && !product;

  if (isLoading || isWaitingForProduct) {
    return <Loading />; 
  }

  // --- Render ---
  return (
    <div className="admin-container">
      <div className="admin-header-container">
        <h1 className="admin-header">
          {id ? 'Edit Product' : 'Create New Product'}
        </h1>
        <button
          type="button"
          className="btn btn-secondary btn-close"
          onClick={() => navigate('/admin/products')}
        >
          <FaTimes size={16} />
          <span>Close</span>
        </button>
      </div>
      
      <form onSubmit={handleSubmit}>
        
        <div className="form-section">
            <div className="form-group">
                <label>Title:</label>
                <input 
                    type="text" 
                    className="form-input"
                    value={title} 
                    onChange={(e) => setTitle(e.target.value)} 
                    required 
                />
            </div>
            <div className="form-group">
                <label>Description:</label>
                <ReactQuill 
                    theme="snow" 
                    value={description} 
                    onChange={setDescription}
                    modules={quillModules}
                    className="quill-editor"
                />
            </div>
            <div className="form-group" ref={categoryWrapperRef}>
              <label>Category:</label>
              <div className="custom-dropdown-wrapper">
                  <input 
                      type="text" 
                      className="form-input"
                      value={category} 
                      onChange={(e) => {
                          setCategory(e.target.value);
                          setShowCategoryDropdown(true);
                      }} 
                      onFocus={() => setShowCategoryDropdown(true)}
                      placeholder="Choose or type a category"
                      autoComplete="off" /* Important: stops browser history from covering your menu */
                      required 
                  />
                  
                  {/* Render the dropdown only if open and has options */}
                  {showCategoryDropdown && filteredCategories.length > 0 && (
                      <ul className="dropdown-options">
                          {filteredCategories.map((option, index) => (
                              <li 
                                  key={index} 
                                  className="dropdown-item"
                                  onClick={() => {
                                      setCategory(option);
                                      setShowCategoryDropdown(false);
                                  }}
                              >
                                  {option}
                              </li>
                          ))}
                      </ul>
                  )}
              </div>
            </div>
            <div className="form-group">
                <label>Display Order:</label>
                <input
                    type="number"
                    className="form-input"
                    value={displayOrder}
                    min="0"
                    onChange={(e) => setDisplayOrder(e.target.value)}
                />
                <small style={{ color: 'var(--color-ink-soft)' }}>
                    Lower numbers appear first in the storefront.
                </small>
            </div>
            <div className="form-group">
                <label>Countries:</label>
                <div className="checkbox-grid">
                    {COUNTRY_OPTIONS.map((option) => (
                        <label key={option.value} className="checkbox-inline">
                            <input
                                type="checkbox"
                                value={option.value}
                                checked={selectedCountries.includes(option.value)}
                                onChange={() => toggleCountrySelection(option.value)}
                            />
                            {option.label}
                        </label>
                    ))}
                </div>
            </div>

            {/* Main Image Uploader */}
            <div className="form-group">
                <label>Main Image:</label>
                <input 
                    type="file" 
                    id="main-image-input" 
                    ref={mainImageInputRef}
                    style={{ display: 'none' }}
                    onChange={handleMainImageChange}
                    required={!id} // Only required for new products
                />
                <div 
                    className="image-uploader" 
                    onClick={() => mainImageInputRef.current.click()}
                >
                    {mainImagePreview ? (
                        <img src={mainImagePreview} alt="Main preview" />
                    ) : (
                        <span>Product thumbnail</span>
                    )}
                </div>
            </div>

            {/* Gallery Image Uploader */}
            <div className="form-group">
                <label>Gallery Images:</label>
                <input 
                    type="file" 
                    id="gallery-images-input" 
                    ref={galleryImagesInputRef}
                    style={{ display: 'none' }}
                    multiple 
                    onChange={handleGalleryImagesChange}
                />
                <div className="gallery-preview-container">
                    {galleryImagePreviews.map((preview, index) => (
                        <div key={index} className="gallery-image-wrapper">
                            <img src={preview} alt={`Gallery preview ${index + 1}`} />
                            <button 
                                type="button" 
                                className="remove-image-btn" 
                                onClick={() => removeGalleryImage(index)}
                            >
                                &times;
                            </button>
                        </div>
                    ))}
                    <div 
                        className="gallery-uploader" 
                        onClick={() => galleryImagesInputRef.current.click()}
                    >
                        <span>Add more</span>
                    </div>
                </div>
            </div>
        </div>
        
        <hr className="form-divider"/>
        
        <div className="form-section">
            <div className="form-group">
                <h2 className="admin-subheader">Product Type</h2>
                <select 
                    className="form-select" 
                    value={productType} 
                    onChange={handleProductTypeChange}
                >
                    <option value="simple">Simple Product</option>
                    <option value="variable">Variable Product</option>
                </select>
            </div>
        </div>

        {productType === 'variable' && (
            <div className="form-section">
                <h3 className="admin-subheader">Product Options</h3>
                {options.map((opt, index) => (
                    <div key={index} style={{ display: 'flex', gap: '15px', marginBottom: '10px' }}>
                        <input 
                            type="text" 
                            className="form-input"
                            placeholder="Option Name (e.g., Size)" 
                            value={opt.name} 
                            onChange={e => handleOptionChange(index, 'name', e.target.value)} 
                        />
                        <input 
                            type="text" 
                            className="form-input"
                            placeholder="Values, comma-separated" 
                            value={opt.values} 
                            onChange={e => handleOptionChange(index, 'values', e.target.value)} 
                        />
                    </div>
                ))}
                <button type="button" className="btn btn-secondary" onClick={addOption}>
                    Add another option
                </button>
            </div>
        )}

        <div className="form-section">
            <h3 className="admin-subheader">
                {productType === 'simple' ? 'Pricing & Stock' : 'Generated Variants'}
            </h3>
            {productType === 'simple' ? (
                 <div style={{ display: 'flex', gap: '15px', marginBottom: '10px' }}>
                    <input 
                        type="number" 
                        className="form-input"
                        placeholder="Price" 
                        value={variants[0]?.price || ''} 
                        onChange={(e) => handleVariantChange(0, 'price', e.target.value)} 
                        required 
                    />
                    <input 
                        type="number" 
                        className="form-input"
                        placeholder="Stock" 
                        value={variants[0]?.countInStock || ''} 
                        onChange={(e) => handleVariantChange(0, 'countInStock', e.target.value)} 
                        required 
                    />
                 </div>
            ) : (
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Active</th>
                            <th>Variation</th>
                            <th>Price</th>
                            <th>Stock</th>
                            <th>Variant Image</th>
                        </tr>
                    </thead>
                    <tbody>
                        {variants.map((variant, index) => (
                            <tr key={index}>
                                <td>
                                    <input 
                                        type="checkbox" 
                                        checked={variant.isActive} 
                                        onChange={(e) => handleVariantChange(index, 'isActive', e.target.checked)} 
                                    />
                                </td>
                                <td>
                                    {variant.optionValues?.map(opt => opt.value).join(' / ') || ''}
                                </td>
                                <td>
                                    <input 
                                        type="number" 
                                        step="0.01" 
                                        value={variant.price} 
                                        disabled={!variant.isActive} 
                                        onChange={(e) => handleVariantChange(index, 'price', e.target.value)} 
                                        style={{ width: '90px' }}
                                    />
                                </td>
                                <td>
                                    <input 
                                        type="number" 
                                        value={variant.countInStock} 
                                        disabled={!variant.isActive} 
                                        onChange={(e) => handleVariantChange(index, 'countInStock', e.target.value)} 
                                        style={{ width: '90px' }}
                                    />
                                </td>
                                <td>
                                    <input 
                                        type="file" 
                                        disabled={!variant.isActive} 
                                        onChange={(e) => handleVariantImageChange(index, e.target.files[0])} 
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
        
        <button type="submit" className="btn btn-primary" disabled={isLoading} style={{ width: '100%', marginTop: '20px', padding: '12px', fontSize: '1.1em' }}>
          {isLoading ? 'Saving...' : (id ? 'Update Product' : 'Create Product')}
        </button>
      </form>
    </div>
  );
}

export default AddProduct;
